import { type TFunction } from 'i18next';
import { uniqBy } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FinderVehicleCondition } from '../../../../../api/types';
import FilterGroup from '../../../StandardApplicationEntrypoint/CarListingPage/CarFilter/FilterGroup';
import { useCarListingState } from '../CarListingStateProvider';

const labelForVehicleCondition = (t: TFunction, condition: FinderVehicleCondition) => {
    switch (condition) {
        case FinderVehicleCondition.New:
            return t('carList:filter.conditions.new');

        case FinderVehicleCondition.Preowned:
            return t('carList:filter.conditions.preowned');

        case FinderVehicleCondition.PorscheApproved:
            return t('carList:filter.conditions.porscheApproved');

        default:
            return undefined;
    }
};

type CarConditionFilterProps = {
    conditions?: FinderVehicleCondition[];
};

const CarConditionFilter = ({ conditions }: CarConditionFilterProps) => {
    const { t } = useTranslation(['carList']);
    const {
        state: { filters, conditionFilter },
        actions,
    } = useCarListingState();

    const onChange = useCallback(
        (value: string[]) => {
            actions.setPage(1);
            actions.setScroll(false);
            actions.setConditionFilter(value.length > 0 ? value : []);
        },
        [actions]
    );

    const options = useMemo(() => {
        const uniqueOptions = uniqBy(
            'value',
            filters?.conditions.map(condition => ({
                label: labelForVehicleCondition(t, condition),
                value: condition,
            }))
        );

        // filter available conditions
        if (conditions?.length) {
            // otherwise filter the conditions
            return uniqueOptions.filter(({ value }) => conditions.includes(value));
        }

        return uniqueOptions;
    }, [filters?.conditions, conditions]);

    if (!options?.length || options.length === 1) {
        return null;
    }

    return (
        <FilterGroup
            onChange={onChange}
            options={options}
            title={t('carList:filter.carConditionTitle')}
            value={conditionFilter || []}
        />
    );
};

export default CarConditionFilter;
